// Sign-in handover from the SCR App: /login?sso=entra#login_hint=<College email>.
// The hint lives in the fragment so it never reaches a server or a log.

export interface ScrAppSignIn {
  loginHint: string | null;
  next: string | null;
}

const COLLEGE_DOMAIN = 'scr.vic.edu.au';
const HINT = /^[A-Za-z0-9._%+'-]{1,64}@scr\.vic\.edu\.au$/i;

/** One leading "/", not "//", no backslash, no control character. */
function isPlainPath(s: string): boolean {
  if (!s.startsWith("/") || s.startsWith("//") || s.includes("\\")) return false;
  for (const ch of s) {
    const c = ch.charCodeAt(0);
    if (c < 32 || c === 127) return false;
  }
  return true;
}

/**
 * A path on this site, or null. Refuses "//host", "/\host", schemes, backslashes and control characters, and
 * the same tricks percent-encoded (checked again after each decoding, so "/%2F/host" and "/%252F%252Fhost" fail).
 * A final parse confirms the path resolves to the origin it is joined to.
 */
export function safeNext(raw: string | null | undefined): string | null {
  if (!raw || !isPlainPath(raw)) return null;
  let s = raw;
  for (let i = 0; i < 3; i++) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(s);
    } catch {
      return null;
    }
    if (!isPlainPath(decoded)) return null;
    if (decoded === s) break;
    s = decoded;
  }
  const base = "https://finance.invalid";
  try {
    if (new URL(raw, base).origin !== base) return null;
  } catch {
    return null;
  }
  return raw;
}

/** Null unless the SCR App asked for sign-in; a failed attempt (?error=…) never auto-starts again. */
export function readScrAppSignIn(search: string, hash: string): ScrAppSignIn | null {
  const q = new URLSearchParams(search);
  if (q.get('sso') !== 'entra') return null;
  if (q.has('error') || q.has('error_description') || q.has('error_code')) return null;
  const h = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const hint = h.get('login_hint');
  return {
    loginHint: hint && HINT.test(hint) ? hint : null,
    next: safeNext(q.get('next')),
  };
}

export function entraQueryParams(s: ScrAppSignIn): Record<string, string> {
  return {
    ...(s.loginHint ? { login_hint: s.loginHint } : {}),
    domain_hint: COLLEGE_DOMAIN,
  };
}
